import { format, isBefore, parse, startOfDay, subSeconds, subYears } from 'date-fns';
import { getTimezoneOffset } from 'date-fns-tz';
import { type Languages, LOCALES } from '@avanta/shared/constants/common';
import { i18n } from '@avanta/shared/i18n';
import { DEFAULT_LANGUAGE } from '@avanta/shared/types/patients';

const MIN_USER_AGE_YEARS = 18;
const MAX_BIRTH_YEAR_LOOKBACK = 100;
const ADULT_BIRTH_DATE_ERROR_MESSAGE = 'common:Form.errors.adultBirthDate';

export type DateInput = string | Date | number;
export const getLanguage = (): string => i18n.language ?? DEFAULT_LANGUAGE;

export const isMoreThanSecondsBefore = (dateTime: string, seconds: number): boolean => {
  const target = new Date(dateTime);
  return isBefore(new Date(), subSeconds(target, seconds));
};

export function parseDate(input: DateInput, formatStr?: string): Date {
  if (input instanceof Date) return input;
  if (typeof input === 'number') return new Date(input);
  if (typeof input === 'string') {
    if (formatStr) {
      const parsed = parse(input, formatStr, new Date());
      if (!isNaN(parsed.getTime())) return parsed;
    }
    const parsed = new Date(input);
    if (!isNaN(parsed.getTime())) return parsed;
  }
  throw new Error(`Invalid date input: ${input}`);
}

/**
 * Formats a date using date-fns
 * @param input Date to format
 * @param mask date-fns format mask (e.g. 'dd/MM/yyyy')
 * @param inputFormat Optional: format to parse the input string
 */
export function formatDate(input: DateInput, mask: string, inputFormat = 'dd/MM/yyyy'): string {
  const date = parseDate(input, inputFormat);
  const locale = LOCALES[getLanguage() as Languages] || LOCALES.en;
  return format(date, mask, { locale });
}

/**
 * Formats to ISO 'yyyy-MM-dd'
 * @example formatDateISO('09/07/2025') // "2025-07-09"
 */
export function formatDateISO(input: DateInput, formatInput = 'dd/MM/yyyy'): string {
  return formatDate(input, 'yyyy-MM-dd', formatInput);
}

/**
 * Formats a date into a human-readable text:
 * - 'd de MMMM de yyyy' for 'es' and 'ca'
 * - 'MMMM d, yyyy' for 'en'
 * @example formatReadable('09/07/2025', 'es') // "9 de julio de 2025"
 */
export function formatDateReadable(input: DateInput, formatInput = 'dd/MM/yyyy'): string {
  const masks: Record<Languages, string> = {
    es: "d 'de' MMMM 'de' yyyy",
    ca: "d 'de' MMMM 'de' yyyy",
    en: 'MMMM d, yyyy',
  };
  return formatDate(input, masks[getLanguage() as Languages], formatInput);
}

/**
 * Extracts the time in 'HH:mm' format
 * @example formatTime('09/07/2025 04:07') // "04:07"
 */
export function formatTime(input: DateInput, formatInput = 'dd/MM/yyyy HH:mm'): string {
  return formatDate(input, 'HH:mm', formatInput);
}

/**
 * Formats a simple date:
 * - 'dd/MM/yyyy' for 'es' and 'ca'
 * - 'MM/dd/yyyy' for 'en'
 * @example formatSimpleDate('09/07/2025') // "07/09/2025"
 */
export function formatDateSimple(input: DateInput, formatInput = 'dd/MM/yyyy'): string {
  const masks: Record<Languages, string> = {
    es: 'dd/MM/yyyy',
    ca: 'dd/MM/yyyy',
    en: 'MM/dd/yyyy',
  };
  return formatDate(input, masks[getLanguage() as Languages], formatInput);
}

const getBirthDateBounds = (): { from: Date; to: Date } => {
  const today = new Date();
  return {
    from: subYears(today, MAX_BIRTH_YEAR_LOOKBACK),
    to: subYears(today, MIN_USER_AGE_YEARS),
  };
};

const isBirthDateWithinBounds = (birthDate: Date): boolean => {
  const { from, to } = getBirthDateBounds();
  return birthDate >= from && birthDate <= to;
};

export const validateAdultBirthDate = (value: unknown): boolean | string => {
  if (!value) return true;
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return false;

  if (!isBirthDateWithinBounds(value)) {
    return ADULT_BIRTH_DATE_ERROR_MESSAGE;
  }

  return true;
};

export const getBirthDateParts = (
  date: Date
): { dayOfBirth: number; monthOfBirth: number; yearOfBirth: number } => ({
  dayOfBirth: date.getDate(),
  monthOfBirth: date.getMonth() + 1,
  yearOfBirth: date.getFullYear(),
});

export const dateFromBirthParts = (
  month?: number,
  year?: number,
  day?: number
): Date | undefined => {
  if (!month || !year) return undefined;
  return new Date(year, month - 1, day ?? 1);
};

const getOffset = (tz: string): string => {
  const offsetMs = getTimezoneOffset(tz);
  const offsetHours = Math.floor(Math.abs(offsetMs) / 3600000);
  const offsetMinutes = Math.floor((Math.abs(offsetMs) % 3600000) / 60000);
  const sign = offsetMs >= 0 ? '+' : '-';
  const minutesSuffix = offsetMinutes === 0 ? '' : `:${String(offsetMinutes).padStart(2, '0')}`;

  return `UTC${sign}${offsetHours}${minutesSuffix}`;
};

export const timezoneOptions = Intl.supportedValuesOf('timeZone').map(tz => ({
  value: tz,
  label: `${tz} (${getOffset(tz)})`,
}));

/**
 * Extracts the time in 'HH:mm' from a unified datetime string (without timezone conversion)
 * @example extractTimeFromUnified('2025-10-14T10:30:00') // "10:30"
 */
export function extractTimeFromUnified(unifiedDateTime: string): string {
  return formatDate(unifiedDateTime, 'HH:mm', "yyyy-MM-dd'T'HH:mm:ss");
}

/**
 * Formats a unified datetime as "13 Octubre, 2026" (es/ca) or "October 13, 2026" (en),
 * with the month name capitalized.
 * @example formatUnifiedDateWithMonth('2026-10-13T14:00:00') // "13 Octubre, 2026"
 */
export function formatUnifiedDateWithMonth(unifiedDateTime: string): string {
  const masks: Record<Languages, string> = {
    es: 'd MMMM, yyyy',
    ca: 'd MMMM, yyyy',
    en: 'MMMM d, yyyy',
  };
  const mask = masks[getLanguage() as Languages] || masks.en;
  const formatted = formatDate(unifiedDateTime, mask, "yyyy-MM-dd'T'HH:mm:ss");
  return formatted.replace(/\p{L}+/u, match => match.charAt(0).toUpperCase() + match.slice(1));
}

export function isPastDate(dateStr: string): boolean {
  const inputDate = startOfDay(new Date(dateStr));
  const today = startOfDay(new Date());

  return isBefore(inputDate, today);
}

/**
 * Formats to unified datetime format 'yyyy-MM-ddTHH:mm:ss'
 * This is the preferred format for all datetime values in the application
 * @example formatDateTimeUnified(new Date(), '10:30') // "2025-10-14T10:30:00"
 * @example formatDateTimeUnified('2025-10-14', '15:45') // "2025-10-14T15:45:00"
 */
export function formatDateTimeUnified(date: DateInput, time?: string): string {
  const parsedDate = parseDate(date);

  if (time) {
    const [h, m] = time.split(':').map(Number);
    const dateCopy = new Date(parsedDate.getTime());
    dateCopy.setHours(h, m, 0, 0);
    return format(dateCopy, "yyyy-MM-dd'T'HH:mm:ss");
  }

  return format(parsedDate, "yyyy-MM-dd'T'HH:mm:ss");
}
